/**
 * Route table and auth gate.
 *
 * Unauthenticated visitors land on the marketing page. Authenticated visitors
 * are routed to the surface their role is configured for. Routes the user
 * lacks a capability for are not rendered in the sidebar, and this guard is
 * the second half of that.
 */
import { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { api } from './api/client';
import type { Me } from './api/types';
import { Shell } from './components/Shell';
import { Loading, ErrorBox } from './components/Metric';
import { lazy, Suspense } from 'react';

/** Marketing landing lives in its own chunk — the app bundle never pays for it. */
const Landing = lazy(() => import('./marketing/Landing'));
import { LoginPage } from './pages/Login';
import { StationPage } from './pages/Station';
import { KioskPage } from './pages/Kiosk';
import { QualityPage } from './pages/Quality';
import { SpcPage } from './pages/Spc';
import { EngineerPage } from './pages/Engineer';
import { PlantPage } from './pages/Plant';
import { RoiPage } from './pages/Roi';
import { AdminPage } from './pages/Admin';
import { RecipesPage } from './pages/Recipes';
import { SubscriptionPage } from './pages/Subscription';
import { AuditPage } from './pages/Audit';
import { SystemPage } from './pages/System';
import { OpsPage } from './pages/Ops';

/** Route → capability required to render it. */
const ROUTE_CAPABILITY: Record<string, string> = {
  '/station': 'station:view',
  '/station/kiosk': 'station:view',
  '/quality': 'quality:metrics:view',
  '/quality/spc': 'quality:metrics:view',
  '/engineer': 'process:rootcause:view',
  '/plant': 'fleet:oee:view',
  '/plant/roi': 'fleet:roi:view',
  '/admin': 'org:inventory:manage',
  '/admin/recipes': 'station:view',
  '/admin/subscription': 'org:subscription:view',
  '/admin/audit': 'quality:audit:view',
  '/admin/system': 'org:inventory:manage',
  '/ops': 'ops:view',
};

function Denied({ me }: { me: Me }) {
  const navigate = useNavigate();
  return (
    <Shell me={me} title="Not permitted">
      <div className="callout warn">
        <strong>Your role cannot open this view</strong>
        Server-side role checks are authoritative. If you believe you should
        have access, an organisation administrator can change your role.
      </div>
      <button className="btn primary" onClick={() => navigate(me.defaultSurface)}>
        Go to my surface
      </button>
    </Shell>
  );
}

function AppRoutes({ me }: { me: Me }) {
  const location = useLocation();
  const caps = new Set(me.capabilities);
  const needed = ROUTE_CAPABILITY[location.pathname];

  const guarded = (el: React.ReactNode, title: string, subtitle?: string) =>
    needed && !caps.has(needed) ? (
      <Denied me={me} />
    ) : (
      <Shell me={me} title={title} subtitle={subtitle}>
        {el}
      </Shell>
    );

  return (
    <Routes>
      <Route path="/" element={<Navigate to={me.defaultSurface} replace />} />
      <Route path="/station" element={guarded(<StationPage me={me} />, 'My station', 'Live inspection at the press')} />
      <Route path="/station/kiosk" element={<KioskPage />} />
      <Route path="/quality" element={guarded(<QualityPage />, 'Quality metrics', 'Escape rate and false-reject rate — the two that matter')} />
      <Route path="/quality/spc" element={guarded(<SpcPage />, 'SPC & process capability', 'Cpk / Ppk from real measurements')} />
      <Route path="/engineer" element={guarded(<EngineerPage />, 'Root-cause correlation', 'Defect + telemetry reasoning, presented as hypotheses')} />
      <Route path="/plant" element={guarded(<PlantPage />, 'Plant overview', 'Fleet quality rollups')} />
      <Route path="/plant/roi" element={guarded(<RoiPage />, 'Return on investment', 'Measured results, plus clearly-labelled reference economics')} />
      <Route path="/admin" element={guarded(<AdminPage />, 'Fleet & inventory', 'Guided onboarding: sites, machines, camera stations')} />
      <Route path="/admin/recipes" element={guarded(<RecipesPage me={me} />, 'Inspection recipes', 'What is bindable here, what ships later, and why')} />
      <Route path="/admin/subscription" element={guarded(<SubscriptionPage />, 'Subscription & entitlements', 'Marketplace status and what it unlocks')} />
      <Route path="/admin/audit" element={guarded(<AuditPage />, 'Audit trail', 'Immutable, timestamped record of every decision')} />
      <Route path="/admin/system" element={guarded(<SystemPage />, 'System health', 'Dependencies, model bindings and integration status')} />
      <Route path="/ops" element={guarded(<OpsPage />, 'Marketplace operations', 'Cross-tenant fleet, catalog stewardship, entitlement overrides')} />
      <Route path="*" element={<Navigate to={me.defaultSurface} replace />} />
    </Routes>
  );
}

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    api
      .get<Me>('/api/me')
      .then((m) => !cancelled && setMe(m))
      .catch((err) => {
        if (cancelled) return;
        // 401 simply means "not signed in" — show the public page.
        if (err && typeof err === 'object' && 'status' in err && err.status === 401) {
          setMe(null);
        } else {
          setError((err as Error).message);
        }
      })
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (checking) {
    return (
      <div style={{ padding: 40 }}>
        <Loading label="Checking session…" />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 40, maxWidth: 600 }}>
        <ErrorBox message={`Could not reach the API: ${error}`} />
      </div>
    );
  }
  if (!me) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Suspense fallback={null}><Landing /></Suspense>} />
      </Routes>
    );
  }
  return <AppRoutes me={me} />;
}

export function Root() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}
